<template>
    <div>
        <PostingPreview :isPreviewOn="isPreviewOn" :lang="lang" :data="form" :theme="theme"
                        v-on:preview-closed="isPreviewOn = false" v-on:publish="handlePublishing()"/>
        <transition
            enter-active-class="transition ease-out duration-500 transform"
            enter-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75 transform"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-show="!isPreviewOn" class="mt-8 bg-white overflow-hidden shadow sm:rounded-lg">
                <ValidationObserver v-slot="{ valid }">
                    <div class="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
                        <div class="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap">
                            <div class="ml-4 mt-2">
                                <h3 class="text-lg leading-6 font-medium text-gray-900">
                                    {{ trans('New job posting') }}
                                </h3>
                            </div>
                            <div class="ml-4 mt-2 flex-shrink-0">
                                <span class="inline-flex rounded-md shadow-sm">
                                    <FormPreviewButton v-on:preview-button-clicked="showPreview(valid)"
                                                       :text="trans('Save & Preview')" :is-valid="valid" />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="px-4 py-5 sm:p-6">
                        <form>
                            <div>
                                <div id="basic-job-info">
                                    <div class="">
                                        <h3 class="text-2xl leading-6 font-medium" :class="`text-${theme.primary_color}`">
                                            {{ trans('Let\'s first talk about the position') }}
                                        </h3>
                                    </div>

                                    <div v-show="hasBackendErrors($page.errors)" class="rounded-md bg-red-50 p-4 mt-4">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20"
                                                     fill="currentColor">
                                                    <path fill-rule="evenodd"
                                                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                          clip-rule="evenodd"/>
                                                </svg>
                                            </div>
                                            <div class="ml-3">
                                                <h3 class="text-sm leading-5 font-medium text-red-800">
                                                    {{ trans('There were errors with your submission') }}
                                                </h3>
                                                <div class="mt-2 text-sm leading-5 text-red-700">
                                                    <ul v-for="errors in $page.errors" class="list-disc pl-5">
                                                        <li v-for="error in errors">{{ error }}</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mt-10 grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-6">
                                        <!-- Title -->
                                        <div class="sm:col-span-4">
                                            <FormLabel labelFor="title" required>{{ trans('Job title') }}</FormLabel>
                                            <ValidationProvider name="form.title" rules="required" v-slot="{ errors }">
                                                <div class="mt-1">
                                                    <InputText id="title" v-model="form.title"/>
                                                </div>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                            <p class="mt-2 text-sm text-gray-500">{{ trans('Example: “Senior PHP Developer”. Title must describe one position.') }}</p>
                                        </div>
                                        <!-- Category -->
                                        <div class="sm:col-span-4" v-if="$page.board.categories.length">
                                            <FormLabel labelFor="category" required>{{ trans('Category') }}</FormLabel>
                                            <ValidationProvider name="form.category_id" rules="required"
                                                                v-slot="{ errors }">

                                                <multiselect v-model="form.categories" :options="$page.board.categories" :multiple="true" label="name" track-by="name"></multiselect>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                        </div>
                                        <!-- Type -->
                                        <div class="flex sm:flex-row flex-col sm:space-x-8 sm:col-span-6">
                                            <div v-for="type in $page.board.job_types" class="flex items-center mb-4 sm:mb-0">
                                                <input :id="`job_type_full_${type.id}`"
                                                       v-model="form.type"
                                                       :value="type.id"
                                                       name="job_type"
                                                       type="radio" checked
                                                       class="form-radio h-6 w-6 transition duration-150 ease-in-out" :class="`text-${theme.primary_color}`"/>
                                                <label :for="`job_type_full_${type.id}`" class="ml-3"><span
                                                    class="block text-sm leading-5 font-medium text-gray-700">{{ trans(type.name) }}</span></label>
                                            </div>
                                        </div>
                                        <!-- Description -->
                                        <div class="sm:col-span-6">
                                            <FormLabel labelFor="description" required>{{ trans('Job description') }}
                                            </FormLabel>
                                            <div class="mt-4">
                                                <ValidationProvider name="form.description" rules="required"
                                                                    v-slot="{ errors }">
                                                    <VueTrix v-model="form.description" inputName="description"
                                                             placeholder="Enter job description" rows="20"/>
                                                    <FormError :errors="errors"/>
                                                </ValidationProvider>
                                            </div>
                                        </div>
                                        <!-- LOCATION -->
                                        <div class="sm:col-span-3">
                                            <FormLabel labelFor="location" required>{{ trans('Location (or Company HQ)') }}
                                            </FormLabel>
                                            <ValidationProvider name="form.location_name" rules="required" v-slot="{ errors }">
                                                <div class="mt-1">
                                                    <InputText id="location" v-model="form.location_name" />
                                                </div>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                            <p class="mt-2 text-sm text-gray-500">{{ trans('Example: “Chicago, IL”, “Berlin, Germany”') }}</p>
                                        </div>
                                        <div class="sm:col-span-3">
                                            <div class="sm:mt-8 sm:ml-3 flex items-center">
                                                <input id="location_remote" v-model="form.location_is_remote"
                                                       type="checkbox"
                                                       class="focus:ring-indigo-500 h-6 w-6 text-indigo-600 border-gray-300 rounded"/>
                                                <div class="ml-3 text-sm leading-5">
                                                    <label for="location_remote" class="text-normal font-semibold text-gray-700">{{
                                                        trans('This position is 100% remote') }}</label>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- How to apply -->
                                        <div class="sm:col-span-4">
                                            <FormLabel labelFor="apply" required>{{ trans('How to apply?') }}
                                            </FormLabel>
                                            <ValidationProvider name="form.application" rules="required"
                                                                v-slot="{ errors }">
                                                <div class="mt-1">
                                                    <InputText id="apply" v-model="form.application" />
                                                </div>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                            <p class="mt-2 text-sm text-gray-500">{{ trans('Link to Application page or Email address.') }}</p>
                                        </div>
                                        <div class="sm:col-span-4">
                                            <button @click.prevent="toggleExtra" type="button" class="inline-flex items-center px-2.5 py-1.5
                                                border border-gray-300 shadow-sm text-xs font-medium rounded
                                                bg-white hover:bg-gray-50 focus:outline-none
                                                focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" :class="extraData? 'text-red-600': 'text-gray-700'">
                                                {{ extraData ? trans('Remove experience and salary') : trans('Add experience and salary') }}
                                            </button>
                                        </div>
                                        <!-- Experience level -->
                                        <div v-if="extraData" class="sm:col-span-3">
                                            <FormLabel labelFor="experience">{{ trans('Experience') }}
                                            </FormLabel>
                                            <div class="mt-1">
                                                <select id="experience" v-model="form.experience"
                                                        class="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md">
                                                    <option v-for="level in $page.board.job_experience_levels" :value="level.id">{{ level.name }}</option>
                                                </select>
                                            </div>
                                        </div>
                                        <!-- Salary -->
                                        <div v-if="extraData" class="sm:col-span-3">
                                            <FormLabel labelFor="salary">{{ trans('Salary') }}</FormLabel>
                                            <div class="mt-1">
                                                <InputText type="text" id="salary" v-model="form.salary"/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div id="company-info" class="mt-8 border-t border-gray-200 pt-8">
                                    <div>
                                        <h3 class="text-2xl leading-6 font-medium" :class="`text-${theme.primary_color}`">
                                            {{ trans('Company Information') }}
                                        </h3>
                                        <p class="mt-1 text-sm leading-5 text-gray-500">
                                            {{ trans('Posted before? Just enter your email and logo, all other info will be pulled in from your last position!') }}
                                        </p>
                                    </div>
                                    <div class="mt-12 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                        <div class="sm:col-span-4">
                                            <FormLabel labelFor="company_email" required>{{ trans('E-Mail Address') }}
                                            </FormLabel>
                                            <ValidationProvider name="form.company_email" rules="email|required"
                                                                v-slot="{ errors }">
                                                <div class="mt-1">
                                                    <input type="text" id="company_email" v-model="form.company_email"
                                                           @keyup="evaluateEmail($event)"
                                                           class="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"/>
                                                </div>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                            <BeatLoader :loading="loader.loading" :color="loader.color"
                                                         :size="loader.size"></BeatLoader>
                                            <p class="mt-2 text-sm text-gray-500" v-if="$page.board.post_confirmation">
                                                {{ trans('Important. We’ll send you confirmation email here.') }}
                                            </p>
                                        </div>

                                        <div v-show="isEmailEvaluated" class="sm:col-span-3">
                                            <FormLabel labelFor="company_name" required>{{ trans('Company name') }}
                                            </FormLabel>
                                            <ValidationProvider name="form.company_name" rules="required"
                                                                v-slot="{ errors }">
                                                <div class="mt-1">
                                                    <InputText type="text" id="company_name" v-model="form.company_name" ref="companyName"/>
                                                </div>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                        </div>

                                        <div v-show="isEmailEvaluated" class="sm:col-span-3">
                                            <FormLabel labelFor="company_url" required>{{ trans('Company website URL')
                                                }}
                                            </FormLabel>
                                            <ValidationProvider name="form.company_url" rules="required"
                                                                v-slot="{ errors }">
                                                <div class="mt-1">
                                                    <InputText type="text" id="company_url" v-model="form.company_url"/>
                                                </div>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                        </div>

                                        <!-- Company Description -->
                                        <div v-show="isEmailEvaluated" class="sm:col-span-6">
                                            <FormLabel labelFor="company_description" required>{{ trans('About company') }}</FormLabel>
                                            <ValidationProvider name="form.company_description" rules="required"
                                                                v-slot="{ errors }">
                                                <div class="mt-1">
                                                    <textarea id="company_description" cols="30" rows="5"
                                                              v-model="form.company_description"
                                                              class="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"/>
                                                </div>
                                                <FormError :errors="errors"/>
                                            </ValidationProvider>
                                        </div>
                                        <!-- Description -->

                                        <div v-show="isEmailEvaluated" class="sm:col-span-6">
                                            <FormLabel labelFor="logo" required>{{ trans('Logo') }}</FormLabel>
                                            <FormUpload v-on:file-selected="handleFile($event)"
                                                        :existing="form.company_existing_logo_absolute"></FormUpload>
                                        </div>

                                    </div>
                                </div>
                                <PostingUpgrades v-if="products.length" :lang="lang" v-model="form.product" :products="products"/>
                            </div>
                            <div class="mt-8 border-t border-gray-200 pt-5">
                                <div class="flex justify-end">
                                    <span class="inline-flex rounded-md shadow-sm">
                                        <FormPreviewButton v-on:preview-button-clicked="showPreview(valid)"
                                                           :text="trans('Save & Preview')" :is-valid="valid" />
                                    </span>
                                </div>
                            </div>
                        </form>
                    </div>
                </ValidationObserver>
            </div>
        </transition>
        <div v-if="$page.board.lang === 'en'" class="mt-6 text-sm text-gray-400 underline flex justify-center"><a target="_blank" href="https://jobroll.co/tips-for-writing-job-posts">job post writing tips</a></div>
    </div>

</template>

<script>
    import PostingLayout from "@/Jobroll/PostingLayout";
    import PostingPreview from "@/Jobroll/PostingPreview";
    import PostingUpgrades from "@/Jobroll/PostingUpgrades";
    import FormLabel from "@/Jobroll/FormLabel";
    import FormError from "@/Jobroll/FormError";
    import FormUpload from "@/Jobroll/FormUpload";
    import FormPreviewButton from "@/Jobroll/FormPreviewButton";

    import VueTrix from "vue-trix";
    import {ValidationProvider, ValidationObserver} from 'vee-validate';
    import {extend} from 'vee-validate';
    import {required, email} from 'vee-validate/dist/rules';
    //import moment from 'moment';
    import BeatLoader from 'vue-spinner/src/BeatLoader.vue';
    import {debounce} from 'lodash';
    import Multiselect from 'vue-multiselect'
    import InputText from "../../../Jobroll/InputText";

    extend('required', {
        ...required,
        message: 'This field is required'
    });
    extend('email', {
        ...email,
        message: 'We need a valid email'
    });

    export default {
        name: "PostingForm",
        layout: PostingLayout,
        components: {
            InputText,
            ValidationProvider,
            ValidationObserver,
            VueTrix,
            BeatLoader,
            Multiselect,
            PostingPreview,
            FormLabel,
            FormError,
            FormUpload,
            FormPreviewButton,
            PostingUpgrades
        },
        props: {
            lang: Object | String,
            hasCategories: Boolean,
            products: Array,
            postingToken: String,
            theme: Object
        },
        data() {
            return {
                extraData: false,
                loader: {
                    color: '#5a67d8',
                    size: '12px',
                    loading: false
                },
                isEmailEvaluated: false,
                isPreviewOn: false,
                form: {
                    title: null,
                    category_ids: this.hasCategories ? [] : [],
                    type: 1,
                    experience: null,
                    salary: null,
                    description: null,
                    location_name: null,
                    location_is_remote: false,
                    application: null,
                    company_email: null,
                    company_name: null,
                    company_url: null,
                    company_description: null,
                    company_existing_logo: null,
                    company_existing_logo_absolute: null,
                    company_uploaded_logo: null,
                    company_uploaded_logo_src: null,
                    created_at: new Date().toISOString().slice(0, 10),
                    product: this.products[0]?.id || 0
                }
            }
        },
        methods: {
            toggleExtra() {
                this.extraData = !this.extraData;

                if (!this.extraData) {
                    this.form.salary = null;
                    this.form.experience = null;
                }
            },
            trans(value) {
                if (Object.keys(this.lang).length === 0) {
                    return value;
                }

                return this.lang[value] || value;
            },
            submit() {
                let data = new FormData();
                data.append('title', this.form.title || '');
                data.append('description', this.form.description || '');
                if (this.hasCategories) {
                    this.form.categories.forEach(function(el) {
                        data.append('categories[]', el.id);
                    });
                }
                if (this.form.salary) {
                    data.append('salary', this.form.salary);
                }
                if (this.form.experience) {
                    data.append('experience', this.form.experience);
                }
                data.append('type', this.form.type || '');
                data.append('application', this.form.application || '');
                data.append('location_name', this.form.location_name || '');
                data.append('location_is_remote', this.form.location_is_remote || '');
                data.append('company_name', this.form.company_name || '');
                data.append('company_description', this.form.company_description || '');
                data.append('company_url', this.form.company_url || '');
                data.append('company_email', this.form.company_email || '');
                data.append('company_existing_logo', this.form.company_existing_logo || '');
                data.append('company_uploaded_logo', this.form.company_uploaded_logo || '');
                data.append('posting_token', this.postingToken || null);
                data.append('product', this.form.product);

                this.$inertia.post('/jobs', data).then(() => {
                    this.isPreviewOn = false;
                });
            },
            getTypeName(typeId) {
                return this.jobTypes[typeId];
            },
            handleFile(event) {
                this.form.company_uploaded_logo = event[0];
                this.form.company_uploaded_logo_src = event[1];
                this.form.company_existing_logo = null;
            },
            showPreview(formValid) {
                if (formValid) {
                    window.scrollTo(0, 0);
                    this.isPreviewOn = true;
                }
            },
            handlePublishing() {
                this.submit();
            },
            evaluateEmail: debounce(function (event) {
                let email = event.target.value;
                this.companyInfoForEmail(email)
            }, 700),
            companyInfoForEmail(email) {
                const re = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
                if (re.test(email.toLowerCase())) {

                    this.loader.loading = true;

                    const url = '/company/info';
                    const token = document.querySelectorAll('meta[name="csrf-token"]')[0].getAttribute('content');

                    fetch(url, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': token
                        },
                        body: JSON.stringify({email: email})
                    }).then((response) => {
                        if (response.ok) {
                            return response.json();
                        }
                        throw new TypeError("Non ok status");
                    }).then((response) => {
                        this.form.company_name = response.data.company.name;
                        this.form.company_description = response.data.company.description;
                        this.form.company_url = response.data.company.url;
                        this.form.company_existing_logo = response.data.company.logo;
                        this.form.company_existing_logo_absolute = response.data.company.logoAbsolute;
                    }).catch((error) => {
                        console.log(error.message)
                    }).finally(() => {
                        this.loader.loading = false;
                        this.isEmailEvaluated = true;
                    });

                }
            },

            hasBackendErrors(errors) {
                return Object.keys(errors).length > 0 && errors.constructor === Object
            }
        }
    }
</script>

<style>
    @import "https://unpkg.com/vue-multiselect@2.1.0/dist/vue-multiselect.min.css";
</style>
